import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { authApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Check, Fingerprint, ArrowRight, Loader2, Delete } from 'lucide-react';

const SetupWallet = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [step, setStep] = useState(1);
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handlePinInput = (digit) => {
    if (step === 2) {
      if (pin.length < 4) {
        setPin(prev => prev + digit);
      }
    } else if (step === 3) {
      if (confirmPin.length < 4) {
        setConfirmPin(prev => prev + digit);
      }
    }
  };

  const handleDelete = () => {
    if (step === 2) {
      setPin(prev => prev.slice(0, -1));
    } else if (step === 3) {
      setConfirmPin(prev => prev.slice(0, -1));
    }
  };

  const handleContinue = async () => {
    if (step === 1) {
      setStep(2);
      return;
    }
    
    if (step === 2) {
      if (pin.length !== 4) {
        toast.error('Please enter a 4-digit PIN');
        return;
      }
      setStep(3);
      return;
    }
    
    if (step === 3) {
      if (confirmPin !== pin) {
        toast.error('PINs do not match');
        setConfirmPin('');
        return;
      }
      
      setIsLoading(true);
      try {
        await authApi.setPin(pin);
        toast.success('PIN set successfully!');
        setStep(4);
      } catch (error) {
        toast.error('Failed to set PIN');
      } finally {
        setIsLoading(false);
      }
      return;
    }
    
    if (step === 4) {
      // Skip biometric for demo
      setStep(5);
      return;
    }
    
    if (step === 5) {
      navigate('/home');
    }
  };

  const NumberPad = () => (
    <div className="grid grid-cols-3 gap-4 max-w-xs mx-auto">
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, '', 0, 'del'].map((item, idx) => (
        <button
          key={idx}
          onClick={() => item === 'del' ? handleDelete() : item !== '' && handlePinInput(String(item))}
          className={`number-pad-btn ${item === '' ? 'invisible' : ''}`}
          disabled={item === ''}
          data-testid={`numpad-${item}`}
        >
          {item === 'del' ? <Delete className="w-6 h-6" /> : item}
        </button>
      ))}
    </div>
  );

  const PinDots = ({ value }) => (
    <div className="flex items-center justify-center gap-4 mb-8">
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className={`w-4 h-4 rounded-full transition-all ${
            value.length > i ? 'bg-primary scale-110' : 'bg-white/20'
          }`}
        />
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Progress */}
      <div className="p-6">
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((s) => (
            <div
              key={s}
              className={`h-1 flex-1 rounded-full transition-all ${
                s <= step ? 'bg-primary' : 'bg-white/10'
              }`}
            />
          ))}
        </div>
        <p className="text-sm text-muted-foreground mt-2">Step {step} of 5</p>
      </div>
      
      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 pb-12">
        <div className="w-full max-w-sm">
          {/* Step 1: Confirm Identity */}
          {step === 1 && (
            <div className="space-y-8 fade-in">
              <div className="text-center space-y-2">
                <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Let's set up your LekeeWallet
                </h1>
                <p className="text-muted-foreground">
                  Confirm your details to continue
                </p>
              </div>
              
              <div className="bg-card border border-white/10 rounded-xl p-6 space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-white/10">
                  <span className="text-muted-foreground">Full Name</span>
                  <span className="font-medium">{user?.full_name}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-white/10">
                  <span className="text-muted-foreground">Email</span>
                  <span className="font-medium">{user?.email}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-white/10">
                  <span className="text-muted-foreground">Phone</span>
                  <span className="font-medium">{user?.phone}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-muted-foreground">Country</span>
                  <span className="font-medium flex items-center gap-2">
                    🇳🇬 Nigeria
                  </span>
                </div>
              </div>
            </div>
          )}
          
          {/* Step 2: Create PIN */}
          {step === 2 && (
            <div className="space-y-8 fade-in">
              <div className="text-center space-y-2">
                <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Create a 4-digit PIN
                </h1>
                <p className="text-muted-foreground">
                  This PIN secures your transactions
                </p>
              </div>
              
              <PinDots value={pin} />
              <NumberPad />
            </div>
          )}
          
          {/* Step 3: Confirm PIN */}
          {step === 3 && (
            <div className="space-y-8 fade-in">
              <div className="text-center space-y-2">
                <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Confirm your PIN
                </h1>
                <p className="text-muted-foreground">
                  Enter your PIN again to confirm
                </p>
              </div>
              
              <PinDots value={confirmPin} />
              <NumberPad />
            </div>
          )}
          
          {/* Step 4: Biometric */}
          {step === 4 && (
            <div className="space-y-8 fade-in text-center">
              <div className="space-y-2">
                <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Enable biometrics?
                </h1>
                <p className="text-muted-foreground">
                  Use fingerprint or face unlock for faster access
                </p>
              </div>
              
              <div className="py-12">
                <div className="w-24 h-24 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
                  <Fingerprint className="w-12 h-12 text-primary" />
                </div>
              </div>
              
              <div className="space-y-3">
                <Button
                  onClick={handleContinue}
                  className="w-full h-12 bg-primary hover:bg-red-600 text-white font-semibold glow-primary"
                  data-testid="enable-biometric-btn"
                >
                  Enable Biometrics
                </Button>
                <Button
                  onClick={() => setStep(5)}
                  variant="ghost"
                  className="w-full h-12"
                  data-testid="skip-biometric-btn"
                >
                  Skip for now
                </Button>
              </div>
            </div>
          )}
          
          {/* Step 5: Success */}
          {step === 5 && (
            <div className="space-y-8 fade-in text-center">
              <div className="py-8">
                <div className="w-20 h-20 mx-auto rounded-full bg-green-500/20 flex items-center justify-center mb-6">
                  <Check className="w-10 h-10 text-green-500" />
                </div>
                <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Your LekeeWallet is ready!
                </h1>
                <p className="text-muted-foreground mb-6">
                  Start sending, receiving, and earning
                </p>
              </div>
              
              <div className="bg-card border border-white/10 rounded-xl p-6 space-y-2">
                <p className="text-sm text-muted-foreground">Your LekeeWallet ID</p>
                <p className="text-xl font-mono font-bold">{user?.lekeewallet_id || 'LKW-XXXX-XXXX-XXXX'}</p>
              </div>
              
              <div className="bg-card border border-white/10 rounded-xl p-6">
                <p className="text-sm text-muted-foreground mb-1">Your Balance</p>
                <p className="text-4xl font-bold balance-text">₦0.00</p>
              </div>
              
              <p className="text-sm text-muted-foreground">
                Complete verification to unlock all features
              </p>
            </div>
          )}
          
          {/* Continue Button */}
          {(step === 1 || step === 5 || (step === 2 && pin.length === 4) || (step === 3 && confirmPin.length === 4)) && step !== 4 && (
            <div className="mt-8">
              <Button
                onClick={handleContinue}
                className="w-full h-12 bg-primary hover:bg-red-600 text-white font-semibold glow-primary"
                disabled={isLoading}
                data-testid="setup-continue-btn"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    {step === 5 ? 'Start using LekeeWallet' : 'Continue'}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SetupWallet;
